import {
  ADDITIONAL_INSTRUCTIONS,
  ADDITIONAL_INSTRUCTION_CODES,
  DOSE_UNITS,
  FREQUENCY_PERIOD_UNITS,
  FREQUENCY_PRESETS,
  UNKNOWN_UNIT,
  getFrequencyPreset,
} from "@/constants/medication-dosage";
import { SNOMED_SYSTEM, UCUM_SYSTEM } from "@/utils/fhir-constants";

import { fhirR4 } from "@smile-cdr/fhirts";

/**
 * Flat, form-friendly representation of a single FHIR `Dosage` element.
 * A medication with more than one step is a taper/titration; each step maps to
 * a `Dosage` element with a `sequence`.
 */
export interface DosageStep {
  /** Dose amount (or the low bound of a range). */
  doseValue: string;
  /** Optional high bound — when present the dose is emitted as a `doseRange`. */
  doseValueHigh: string;
  doseUnit: string;
  /** One of FREQUENCY_PRESETS values (incl. "custom") or "". */
  frequencyPreset: string;
  frequencyTimes: string;
  frequencyEvery: string;
  /**
   * Optional high bound of the interval ("every 4 to 6 hours"), mapped to
   * `timing.repeat.periodMax`. Only meaningful alongside `frequencyEvery`.
   */
  frequencyEveryHigh: string;
  frequencyUnit: string;
  /**
   * "As needed" (PRN). Purely additive: it sets `asNeededBoolean` and nothing
   * else. The per-period count stays in `timing.repeat.frequency` whether or
   * not PRN is on, so "twice daily as needed" is `frequency: 2` plus the flag.
   * `frequencyMax` is never authored — see VMD-699.
   */
  asNeeded: boolean;
  /**
   * Infusion rate, e.g. "100 mL per hour" → `doseAndRate[].rateRatio`. The
   * denominator is always one of `ratePeriodUnit`, matching `maxDosePerPeriod`.
   * Only the Ratio form is authored; a stored `rateQuantity` or `rateRange` is
   * something the form cannot show, so it is carried through instead.
   */
  rateValue: string;
  rateUnit: string;
  ratePeriodUnit: string;
  /** Optional amount ceiling ("max 8 tablets per day") → `maxDosePerPeriod`. */
  maxDoseValue: string;
  maxDoseUnit: string;
  maxDosePeriodUnit: string;
  /** EventTiming codes (MORN | AFT | EVE | HS | AC | PC). */
  timing: string[];
  /** Whether the free-text "custom" timing field is enabled (UI state). */
  customTimingEnabled: boolean;
  /** Free-text timing instruction, mapped to dosage.patientInstruction. */
  customTiming: string;
  /** Course-length value, mapped to timing.repeat.boundsDuration. */
  durationValue: string;
  durationUnit: string;
  /**
   * Total number of doses in the course ("three doses in all") →
   * `timing.repeat.count`. Blank on a record whose repeat is exactly the
   * one-time-only shape, where the count belongs to that preset instead.
   */
  totalDoses: string;
  /**
   * `Dosage.text` — the free-text dosage instruction (SIG). Authored by a human
   * and never generated from the structured fields: the two describe the same
   * medication but neither derives from the other, so nothing here infers who
   * wrote a stored SIG or rewrites one on save.
   */
  text: string;
  /**
   * `Dosage.additionalInstruction` — supplemental instructions and warnings
   * ("with meals", "may cause drowsiness"), held as SNOMED codes. Codes the
   * picker does not offer are round-tripped untouched rather than dropped.
   */
  additionalInstruction: string[];
}

export const emptyDosageStep = (): DosageStep => ({
  doseValue: "",
  doseValueHigh: "",
  doseUnit: "",
  frequencyPreset: "",
  frequencyTimes: "",
  frequencyEvery: "",
  frequencyEveryHigh: "",
  frequencyUnit: "",
  asNeeded: false,
  rateValue: "",
  rateUnit: "",
  ratePeriodUnit: "",
  maxDoseValue: "",
  maxDoseUnit: "",
  maxDosePeriodUnit: "",
  timing: [],
  customTimingEnabled: false,
  customTiming: "",
  durationValue: "",
  durationUnit: "",
  totalDoses: "",
  text: "",
  additionalInstruction: [],
});

const DURATION_UNIT_LABELS: Record<string, string> = {
  min: "minute",
  h: "hour",
  d: "day",
  wk: "week",
  mo: "month",
  a: "year",
};

/** True when a step carries any meaningful clinical content. */
export const stepHasContent = (step: DosageStep): boolean =>
  Boolean(
    step.doseValue ||
    step.frequencyPreset ||
    step.asNeeded ||
    step.rateValue ||
    step.maxDoseValue ||
    step.timing.length > 0 ||
    (step.customTimingEnabled && step.customTiming) ||
    step.durationValue ||
    step.totalDoses ||
    step.text ||
    step.additionalInstruction.length > 0
  );

const findUnit = (unit: string) => DOSE_UNITS.find(u => u.value === unit);

const buildQuantity = (value: number, unit: string): fhirR4.Quantity => {
  // No unit means no unit — an empty `unit`/`code` is not a valid FHIR
  // primitive, and asserting the UCUM system for a unit we do not have is worse
  // than saying nothing.
  if (!unit) {
    return { value };
  }
  const def = findUnit(unit);
  // Counted forms (tablet, capsule, ...) are not UCUM units, and neither is a
  // unit we do not recognise at all. Strict rather than defaulting to true,
  // which fabricated a code for a legacy 'tab' or a label-cased 'mEq'.
  const isUcum = def?.ucum === true;
  return {
    value,
    unit,
    ...(isUcum && { system: UCUM_SYSTEM, code: def?.ucumCode || unit }),
  };
};

const parseNumber = (raw: string): number | undefined => {
  if (!raw) {
    return undefined;
  }
  const value = parseFloat(raw);
  return Number.isNaN(value) ? undefined : value;
};

/**
 * `Timing.repeat` keys the form owns. Anything else on a stored repeat
 * (`dayOfWeek`, `timeOfDay`, `offset`, the other `bounds[x]` choices …) is
 * carried through untouched — the form cannot express it, which is a reason to
 * leave it alone, not to delete it.
 */
const FORM_OWNED_REPEAT_KEYS = [
  "frequency",
  "period",
  "periodMax",
  "periodUnit",
  "when",
  "boundsDuration",
  "count",
] as const;

/**
 * True when a stored repeat is exactly the "one time only" shape.
 *
 * `count` is form-owned either way now that Total doses exposes it, but which
 * control owns it differs: on this shape it is the one-time-only preset's own
 * field, so hydration leaves Total doses blank rather than showing the same 1
 * in two places, and `buildRepeat` lets the preset write it back.
 */
const storedIsOneTimeOnly = (repeat?: fhirR4.TimingRepeat): boolean =>
  repeat?.count === 1 && !repeat.frequency && !repeat.frequencyMax && !repeat.period;

const buildRepeat = (step: DosageStep, stored?: fhirR4.TimingRepeat): fhirR4.TimingRepeat | undefined => {
  // Start from what is stored, minus the keys this form is about to re-author,
  // so clearing a control removes its field instead of leaving a stale value.
  const repeat: fhirR4.TimingRepeat = { ...stored };
  for (const key of FORM_OWNED_REPEAT_KEYS) {
    delete repeat[key];
  }

  // The per-period count always lands in `frequency`, PRN or not. PRN is purely
  // additive (`asNeededBoolean`) and does not change how the count is stored —
  // VMD-699 reversed the earlier ceiling rule, because PRN-with-`frequency`
  // outnumbers PRN-with-`frequencyMax` ~310:8 in the real data. `frequencyMax`
  // is never authored here; a stored one is preserved by the merge above.
  if (step.frequencyPreset === "custom") {
    const times = parseNumber(step.frequencyTimes);
    if (times !== undefined) {
      repeat.frequency = times;
    }
    // R4 invariant tim-2: a period is meaningless without its unit, so neither
    // bound is emitted until the unit is set.
    const every = parseNumber(step.frequencyEvery);
    if (every !== undefined && step.frequencyUnit) {
      repeat.period = every;
      const everyHigh = parseNumber(step.frequencyEveryHigh);
      // periodMax is only meaningful as the upper bound of an interval range.
      if (everyHigh !== undefined && everyHigh > every) {
        repeat.periodMax = everyHigh;
      }
    }
    if (step.frequencyUnit) {
      repeat.periodUnit = step.frequencyUnit as fhirR4.TimingRepeat.PeriodUnitEnum;
    }
  } else if (step.frequencyPreset) {
    const preset = FREQUENCY_PRESETS.find(p => p.value === step.frequencyPreset);
    if (preset?.count !== null && preset?.count !== undefined) {
      repeat.count = preset.count;
    }
    if (preset && preset.frequency !== null) {
      repeat.frequency = preset.frequency;
      repeat.period = preset.period;
      repeat.periodUnit = preset.periodUnit as fhirR4.TimingRepeat.PeriodUnitEnum;
    }
  }

  // Total doses owns `count` except on the one-time-only preset, whose whole
  // meaning is a count of 1 — that preset wins so the two controls cannot
  // contradict each other. Hydration keeps them apart: a repeat that is exactly
  // the one-time-only shape leaves Total doses blank.
  const totalDoses = parseNumber(step.totalDoses);
  if (totalDoses !== undefined && repeat.count === undefined) {
    repeat.count = totalDoses;
  }

  if (step.timing.length > 0) {
    repeat.when = step.timing;
  }

  if (step.durationValue && step.durationUnit) {
    const value = parseNumber(step.durationValue);
    if (value !== undefined) {
      repeat.boundsDuration = {
        value,
        unit: step.durationUnit,
        system: UCUM_SYSTEM,
        code: step.durationUnit,
      };
    }
  }

  return Object.keys(repeat).length ? repeat : undefined;
};

/**
 * Supplemental instructions as CodeableConcepts. Stored concepts the picker
 * cannot express — an unrecognised code, or a text-only concept — are carried
 * through untouched; only the codes this form offers are re-authored from the
 * selection, so deselecting one removes it and nothing else is disturbed.
 */
const buildAdditionalInstruction = (step: DosageStep, stored?: fhirR4.CodeableConcept[]): fhirR4.CodeableConcept[] => {
  const isOffered = (concept: fhirR4.CodeableConcept) =>
    concept.coding?.some(c => c.system === SNOMED_SYSTEM && ADDITIONAL_INSTRUCTION_CODES.has(c.code ?? ""));

  const preserved = (stored ?? []).filter(concept => !isOffered(concept));

  const selected = step.additionalInstruction.map(code => {
    // Reuse the stored concept when there is one so any extra codings or text
    // ride along rather than being flattened to our own coding.
    const existing = (stored ?? []).find(concept =>
      concept.coding?.some(c => c.system === SNOMED_SYSTEM && c.code === code)
    );
    if (existing) {
      return existing;
    }
    const option = ADDITIONAL_INSTRUCTIONS.find(i => i.code === code);
    return {
      coding: [{ system: SNOMED_SYSTEM, code, ...(option && { display: option.display }) }],
      ...(option && { text: option.display }),
    };
  });

  return [...preserved, ...selected];
};

/**
 * Infusion rate as a FHIR `Ratio` — "100 mL per hour" is 100 mL over a
 * denominator of 1 hour. Same shape as `maxDosePerPeriod`, but it lives on the
 * `doseAndRate` element beside the dose rather than on the Dosage.
 */
const buildRateRatio = (step: DosageStep): fhirR4.Ratio | undefined => {
  const value = parseNumber(step.rateValue);
  if (value === undefined || !step.ratePeriodUnit) {
    return undefined;
  }
  return {
    numerator: buildQuantity(value, step.rateUnit),
    denominator: {
      value: 1,
      unit: step.ratePeriodUnit,
      system: UCUM_SYSTEM,
      code: step.ratePeriodUnit,
    },
  };
};

/**
 * Amount ceiling as a FHIR `Ratio` — "max 8 tablets per day" is 8 tablets over
 * a denominator of 1 day.
 */
const buildMaxDosePerPeriod = (step: DosageStep): fhirR4.Ratio | undefined => {
  const value = parseNumber(step.maxDoseValue);
  if (value === undefined || !step.maxDosePeriodUnit) {
    return undefined;
  }
  return {
    numerator: buildQuantity(value, step.maxDoseUnit),
    denominator: {
      value: 1,
      unit: step.maxDosePeriodUnit,
      system: UCUM_SYSTEM,
      code: step.maxDosePeriodUnit,
    },
  };
};

/**
 * Resolves a display label for an option value. The defaults use the English
 * constant labels (for the stored `dosage.text`, which is kept in English); UI
 * callers (e.g. the table) pass a `translate`-backed resolver to localize.
 */
type LabelResolver = (value: string) => string;
/**
 * Resolves a dose-unit label for a given quantity. Pluralization is the
 * resolver's job — UI callers use i18next count pluralization (`translate(key,
 * { count })`), which applies the correct per-locale CLDR plural form. The
 * default returns the count-agnostic English constant label for stored text.
 */
type UnitLabelResolver = (unitValue: string, count: number) => string;

const defaultUnitLabel: UnitLabelResolver = unitValue =>
  DOSE_UNITS.find(u => u.value === unitValue)?.label ?? unitValue;
const defaultFrequencyLabel: LabelResolver = presetValue =>
  FREQUENCY_PRESETS.find(p => p.value === presetValue)?.label ?? "";

/** Translate signature (matches Refine's) used for the summary connectives. */
export type SummaryTranslate = (key: string, params?: Record<string, unknown>) => string;

/**
 * English fallbacks for the connective words. Used when no `translate` is
 * supplied — tests and any non-UI caller. UI callers pass a real translate so
 * the preview and the table localize, since these strings reach the screen.
 */
const SUMMARY_EN: Record<string, string> = {
  "pages.medications.dosage.summary.range": "{{low}} to {{high}}",
  "pages.medications.dosage.summary.asNeeded": "as needed",
  "pages.medications.dosage.summary.asNeededWith": "{{schedule}}, as needed",
  "pages.medications.dosage.summary.every": "every {{interval}} {{unit}}",
  "pages.medications.dosage.summary.timesEvery": "{{times}}x every {{interval}} {{unit}}",
  "pages.medications.dosage.summary.times": "{{times}}x",
  "pages.medications.dosage.summary.max": "max {{amount}} per {{period}}",
  "pages.medications.dosage.summary.duration": "for {{value}} {{unit}}",
  "pages.medications.dosage.summary.rate": "at {{amount}} per {{period}}",
  "pages.medications.dosage.summary.totalDoses_one": "{{count}} dose in total",
  "pages.medications.dosage.summary.totalDoses_other": "{{count}} doses in total",
  "pages.medications.dosage.summary.unknownUnit": "(unit unknown)",
};

const fallbackTranslate: SummaryTranslate = (key, params) => {
  // Mimics i18next's English plural resolution so a pluralized key behaves the
  // same whether or not a real translate was supplied.
  const plural = typeof params?.count === "number" ? `${key}_${params.count === 1 ? "one" : "other"}` : undefined;
  const template = (plural && SUMMARY_EN[plural]) ?? SUMMARY_EN[key] ?? key;
  return template.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => String(params?.[name] ?? ""));
};

/**
 * Unit label for one amount on a summary line.
 *
 * The unknown unit is spelled out here rather than in `resolveUnitLabel`,
 * because that resolver is supplied by every caller and each of them would
 * otherwise need the same special case. Printing its bare value would read as a
 * unit called "unknown" instead of an absent one.
 */
const summaryUnitLabel = (
  unit: string,
  count: number,
  translate: SummaryTranslate,
  resolveUnitLabel: UnitLabelResolver
): string => {
  if (!unit) {
    return "";
  }
  return unit === UNKNOWN_UNIT
    ? translate("pages.medications.dosage.summary.unknownUnit")
    : resolveUnitLabel(unit, count);
};

/** Shared options for the summary helpers. */
export interface SummaryOptions {
  translate?: SummaryTranslate;
  resolveUnitLabel?: UnitLabelResolver;
  resolveFrequencyLabel?: LabelResolver;
  /** Course-duration unit label, e.g. ("d", 7) -> "days". */
  resolveDurationLabel?: (unitValue: string, count: number) => string;
}

/**
 * Dose portion of a step summary, e.g. "50 mg", "2 capsules" or "25/50 mg".
 * The unit label (incl. pluralization) is delegated to `resolveUnitLabel`; the
 * quantity is forwarded as `count` (the high bound for ranges).
 */
export const summarizeDose = (step: DosageStep, options: SummaryOptions = {}): string => {
  if (!step.doseValue) {
    return "";
  }
  const { translate = fallbackTranslate, resolveUnitLabel = defaultUnitLabel } = options;
  const count = Number(step.doseValueHigh || step.doseValue);
  const label = summaryUnitLabel(step.doseUnit, count, translate, resolveUnitLabel);
  const unit = label ? ` ${label}` : "";
  // Only render a range the serializer would actually store (see buildQuantity /
  // stepsToDosage) — previewing a bound that gets discarded is a lie.
  const high = parseNumber(step.doseValueHigh);
  const low = parseNumber(step.doseValue);
  const isRange = high !== undefined && low !== undefined && high > low;
  return isRange
    ? `${translate("pages.medications.dosage.summary.range", { low: step.doseValue, high: step.doseValueHigh })}${unit}`
    : `${step.doseValue}${unit}`;
};

/**
 * Schedule text without the PRN wrapper, e.g. "once daily", "every 4 to 6 h".
 * Mirrors `buildRepeat` exactly — anything rendered here is something that gets
 * stored, and anything stored is rendered here.
 */
const summarizeSchedule = (step: DosageStep, options: Required<Pick<SummaryOptions, "translate">> & SummaryOptions) => {
  const { translate, resolveFrequencyLabel = defaultFrequencyLabel } = options;

  if (step.frequencyPreset === "custom") {
    // A period needs its unit to be stored at all (tim-2), so without one there
    // is no interval to describe — but a bare count still is.
    const hasInterval = Boolean(step.frequencyEvery && step.frequencyUnit);
    if (!hasInterval) {
      return step.frequencyTimes
        ? translate("pages.medications.dosage.summary.times", { times: step.frequencyTimes })
        : "";
    }
    const every = parseNumber(step.frequencyEvery);
    const everyHigh = parseNumber(step.frequencyEveryHigh);
    // periodMax is only stored when it is above the low bound.
    const interval =
      every !== undefined && everyHigh !== undefined && everyHigh > every
        ? translate("pages.medications.dosage.summary.range", {
            low: step.frequencyEvery,
            high: step.frequencyEveryHigh,
          })
        : step.frequencyEvery;
    return step.frequencyTimes
      ? translate("pages.medications.dosage.summary.timesEvery", {
          times: step.frequencyTimes,
          interval,
          unit: step.frequencyUnit,
        })
      : translate("pages.medications.dosage.summary.every", { interval, unit: step.frequencyUnit });
  }

  return step.frequencyPreset ? resolveFrequencyLabel(step.frequencyPreset).toLowerCase() : "";
};

/**
 * Frequency portion of a step summary, e.g. "once daily", "every 4 to 6 h",
 * "as needed", "twice daily, as needed".
 */
export const summarizeFrequency = (step: DosageStep, options: SummaryOptions = {}): string => {
  const translate = options.translate ?? fallbackTranslate;
  const schedule = summarizeSchedule(step, { ...options, translate });
  if (!step.asNeeded) {
    return schedule;
  }
  // PRN sits alongside the schedule rather than reinterpreting it as a ceiling.
  return schedule
    ? translate("pages.medications.dosage.summary.asNeededWith", { schedule })
    : translate("pages.medications.dosage.summary.asNeeded");
};

/** Infusion-rate portion of a step summary, e.g. "at 100 mL per hour". */
export const summarizeRate = (step: DosageStep, options: SummaryOptions = {}): string => {
  if (!step.rateValue || !step.ratePeriodUnit) {
    return "";
  }
  const { translate = fallbackTranslate, resolveUnitLabel = defaultUnitLabel } = options;
  const label = summaryUnitLabel(step.rateUnit, Number(step.rateValue), translate, resolveUnitLabel);
  const amount = label ? `${step.rateValue} ${label}` : step.rateValue;
  const period = DURATION_UNIT_LABELS[step.ratePeriodUnit] ?? step.ratePeriodUnit;
  return translate("pages.medications.dosage.summary.rate", { amount, period });
};

/** Amount-ceiling portion of a step summary, e.g. "max 8 tablets per day". */
export const summarizeMaxDose = (step: DosageStep, options: SummaryOptions = {}): string => {
  if (!step.maxDoseValue || !step.maxDosePeriodUnit) {
    return "";
  }
  const { translate = fallbackTranslate, resolveUnitLabel = defaultUnitLabel } = options;
  const unitValue = step.maxDoseUnit || step.doseUnit;
  const label = summaryUnitLabel(unitValue, Number(step.maxDoseValue), translate, resolveUnitLabel);
  const amount = label ? `${step.maxDoseValue} ${label}` : step.maxDoseValue;
  const period = DURATION_UNIT_LABELS[step.maxDosePeriodUnit] ?? step.maxDosePeriodUnit;
  return translate("pages.medications.dosage.summary.max", { amount, period });
};

/**
 * Human-readable one-line summary of a step's structured fields.
 *
 * Display only — it renders the form preview and the medications table, and is
 * never written to `Dosage.text`. That field holds the human-authored SIG, which
 * this neither derives from nor overwrites.
 */
export const summarizeStep = (step: DosageStep, route?: string, options: SummaryOptions = {}): string => {
  const { translate = fallbackTranslate, resolveDurationLabel } = options;
  const parts: string[] = [];

  const dose = summarizeDose(step, options);
  if (dose) {
    parts.push(dose);
  }

  const frequency = summarizeFrequency(step, options);
  if (frequency) {
    parts.push(frequency);
  }

  const rate = summarizeRate(step, options);
  if (rate) {
    parts.push(rate);
  }

  const maxDose = summarizeMaxDose(step, options);
  if (maxDose) {
    parts.push(maxDose);
  }

  if (step.totalDoses) {
    parts.push(translate("pages.medications.dosage.summary.totalDoses", { count: Number(step.totalDoses) }));
  }

  if (step.durationValue && step.durationUnit) {
    const count = Number(step.durationValue);
    const base = DURATION_UNIT_LABELS[step.durationUnit] ?? step.durationUnit;
    const label = resolveDurationLabel?.(step.durationUnit, count) ?? (count === 1 ? base : `${base}s`);
    parts.push(translate("pages.medications.dosage.summary.duration", { value: step.durationValue, unit: label }));
  }

  if (route) {
    parts.push(route);
  }

  return parts.join(", ");
};

/**
 * Fields of a step that carry input the serializer would otherwise discard.
 * Keyed by the `DosageStep` field the message belongs against, so the editor can
 * put each error on its own control.
 */
export type DosageStepErrors = Partial<Record<keyof DosageStep, string>>;

/**
 * Flags input that would be silently dropped on save. Each of these is a value
 * the clinician typed that the FHIR mapping cannot carry, so the form blocks
 * submit and says why rather than quietly losing it.
 *
 * Returns translation keys, not sentences — the editor renders them.
 */
export const validateDosageStep = (step: DosageStep): DosageStepErrors => {
  const errors: DosageStepErrors = {};

  const low = parseNumber(step.doseValue);
  const high = parseNumber(step.doseValueHigh);
  if (high !== undefined && (low === undefined || high <= low)) {
    errors.doseValueHigh = "pages.medications.dosage.errors.doseRangeOrder";
  }
  // No rule for a missing dose unit any more: the quantity's noun comes from the
  // Form picker, and leaving that unset is a real answer that stores the
  // `unknown` literal rather than something to block on.

  if (step.frequencyPreset === "custom") {
    const every = parseNumber(step.frequencyEvery);
    const everyHigh = parseNumber(step.frequencyEveryHigh);
    if (every !== undefined && !step.frequencyUnit) {
      errors.frequencyUnit = "pages.medications.dosage.errors.periodUnitRequired";
    }
    if (everyHigh !== undefined && (every === undefined || everyHigh <= every)) {
      errors.frequencyEveryHigh = "pages.medications.dosage.errors.intervalOrder";
    }
  }

  if (step.maxDoseValue && !step.maxDosePeriodUnit) {
    errors.maxDosePeriodUnit = "pages.medications.dosage.errors.maxPeriodRequired";
  }

  // A ceiling with no unit is not a ceiling. It used to write an unlabelled
  // numerator, which on a full PUT silently degrades a limit the record already
  // had, so it blocks instead. §3 bars `unknown` here, hence the only way out
  // being a real form.
  if (step.maxDoseValue && !step.maxDoseUnit) {
    errors.maxDoseUnit = "pages.medications.dosage.errors.maxUnitNeedsForm";
  }

  // A rate is a Ratio; without its denominator period there is nothing to store.
  if (step.rateValue && !step.ratePeriodUnit) {
    errors.ratePeriodUnit = "pages.medications.dosage.errors.ratePeriodRequired";
  }
  if (step.rateValue && !step.rateUnit) {
    errors.rateUnit = "pages.medications.dosage.errors.rateUnitRequired";
  }

  // `Timing.repeat.count` is a positiveInt — a fraction or a zero is not a
  // number of doses, and would be rejected by the server rather than stored.
  const totalDoses = parseNumber(step.totalDoses);
  if (totalDoses !== undefined && (!Number.isInteger(totalDoses) || totalDoses < 1)) {
    errors.totalDoses = "pages.medications.dosage.errors.totalDosesPositiveInt";
  }

  return errors;
};

/** True when every step is free of the errors above. */
export const dosageStepsAreValid = (steps: DosageStep[]): boolean =>
  steps.every(step => Object.keys(validateDosageStep(step)).length === 0);

export interface DosageContext {
  route?: string;
  instructions?: string;
  /**
   * The `dosage[]` currently stored on the resource. Form-owned fields are
   * re-authored on top of it; everything else survives untouched.
   */
  existing?: ReadonlyArray<fhirR4.Dosage | null | undefined> | null;
}

/**
 * `Dosage` keys the form owns. A stored element keeps everything else —
 * `asNeededCodeableConcept`, `site`, `method`, `maxDosePerAdministration` and
 * any extensions — because the form cannot represent them, not because they
 * should be discarded.
 */
const FORM_OWNED_DOSAGE_KEYS = [
  "sequence",
  "doseAndRate",
  "asNeededBoolean",
  "timing",
  "maxDosePerPeriod",
  "route",
  "patientInstruction",
  "text",
  "additionalInstruction",
] as const;

/**
 * Applies the two form fields that live outside a step. Mutates `d` in place;
 * both are form-owned, so they have already been stripped by the caller and an
 * absent value means the control is empty rather than untouched.
 */
const applyContext = (d: fhirR4.Dosage, route?: string, instruction?: string): void => {
  if (route) {
    d.route = { text: route };
  }
  if (instruction) {
    d.patientInstruction = instruction;
  }
};

/**
 * Build a FHIR `dosage[]` from form steps, merging onto `existing` where the
 * resource already has one. Multiple content-bearing steps are emitted with a
 * `sequence` (a taper/titration). `route` is applied to every element;
 * `instructions` to the first element only (matching the form).
 *
 * A step the clinician has emptied does not necessarily delete its element: if
 * the stored one still carries fields this form cannot show, the remainder is
 * emitted so those survive. Only an element with nothing left at all goes.
 */
export const stepsToDosage = (
  steps: DosageStep[] | undefined,
  { route, instructions, existing }: DosageContext = {}
): fhirR4.Dosage[] => {
  const all = steps ?? [];
  const useSequence = all.filter(stepHasContent).length > 1;

  const dosage: fhirR4.Dosage[] = [];
  let sequence = 0;

  all.forEach((step, index) => {
    // Indexed by the step's real position, not its position among the
    // content-bearing ones: emptying an earlier step must not shift a later one
    // onto the wrong stored element.
    const stored = existing?.[index] ?? undefined;
    // Carry the stored element forward, minus the fields being re-authored, so
    // clearing a control removes its field rather than leaving a stale value.
    const d: fhirR4.Dosage = { ...stored };
    for (const key of FORM_OWNED_DOSAGE_KEYS) {
      delete d[key];
    }

    if (!stepHasContent(step)) {
      // Nothing left in the form for this element, but `d` may still hold
      // `site`, `method`, extensions and the like. Unticking a gate empties the
      // step in one click, and that must not take unmodelled data with it.
      applyContext(d, route, index === 0 ? instructions : undefined);
      if (Object.keys(d).length) {
        dosage.push(d);
      }
      return;
    }

    sequence += 1;
    if (useSequence) {
      d.sequence = sequence;
    }

    // R4 puts `dose[x]` and `rate[x]` on the same `doseAndRate` element, so an
    // infused dose is one entry carrying both, not two entries.
    const storedRate = stored?.doseAndRate?.[0];
    const doseAndRate: fhirR4.DosageDoseAndRate = {};

    if (step.doseValue) {
      const low = parseNumber(step.doseValue);
      const high = parseNumber(step.doseValueHigh);
      if (low !== undefined) {
        // A high bound only makes a range when it is actually above the low one.
        // `validateDosageStep` blocks the inverted case, so this is a guard
        // against writing a nonsense range rather than a silent discard.
        if (high !== undefined && high > low) {
          doseAndRate.doseRange = {
            low: buildQuantity(low, step.doseUnit),
            high: buildQuantity(high, step.doseUnit),
          };
        } else {
          doseAndRate.doseQuantity = buildQuantity(low, step.doseUnit);
        }
      }
    }

    const rateRatio = buildRateRatio(step);
    if (rateRatio) {
      doseAndRate.rateRatio = rateRatio;
    } else if (storedRate?.rateQuantity ?? storedRate?.rateRange) {
      // The form only authors the Ratio form. A rate stored as a Quantity or a
      // Range is something it cannot show, which is a reason to leave it alone
      // rather than to drop it.
      if (storedRate.rateQuantity) {
        doseAndRate.rateQuantity = storedRate.rateQuantity;
      }
      if (storedRate.rateRange) {
        doseAndRate.rateRange = storedRate.rateRange;
      }
    }

    if (Object.keys(doseAndRate).length) {
      // `type` qualifies the dose ("ordered" vs "calculated") and is not
      // something the form sets, so it rides along with whatever it describes.
      if (storedRate?.type) {
        doseAndRate.type = storedRate.type;
      }
      d.doseAndRate = [doseAndRate];
    }

    if (step.asNeeded) {
      d.asNeededBoolean = true;
    }

    const repeat = buildRepeat(step, stored?.timing?.repeat);
    if (repeat) {
      d.timing = { ...stored?.timing, repeat };
    } else if (stored?.timing) {
      // Keep any non-repeat Timing content (event, code) even with no schedule.
      const { repeat: _dropped, ...timing } = stored.timing;
      if (Object.keys(timing).length) {
        d.timing = timing;
      }
    }

    const maxDosePerPeriod = buildMaxDosePerPeriod(step);
    if (maxDosePerPeriod) {
      d.maxDosePerPeriod = maxDosePerPeriod;
    }

    // Free-text custom timing (per step) takes precedence over the shared
    // DosageContext instructions, which only apply to the first element.
    const customInstruction = step.customTimingEnabled && step.customTiming ? step.customTiming : undefined;
    applyContext(d, route, customInstruction ?? (index === 0 ? instructions : undefined));

    // The SIG is whatever the clinician typed, verbatim. It is never generated
    // from the structured fields, so it never goes stale against them and an
    // edit elsewhere in the form cannot rewrite it.
    if (step.text) {
      d.text = step.text;
    }

    const additionalInstruction = buildAdditionalInstruction(step, stored?.additionalInstruction);
    if (additionalInstruction.length) {
      d.additionalInstruction = additionalInstruction;
    }

    dosage.push(d);
  });

  // Route and instructions are worth an element of their own — a medication can
  // be recorded as "oral, as directed" with no structured dose at all.
  if (!dosage.length && (route || instructions)) {
    const d: fhirR4.Dosage = { ...(existing?.[0] ?? undefined) };
    for (const key of FORM_OWNED_DOSAGE_KEYS) {
      delete d[key];
    }
    applyContext(d, route, instructions);
    return [d];
  }

  return dosage;
};

/** Inverse of stepsToDosage: hydrate form steps from a FHIR `dosage[]`. */
export const dosageToSteps = (dosage?: ReadonlyArray<fhirR4.Dosage | null | undefined> | null): DosageStep[] => {
  if (!dosage || !dosage.length) {
    return [emptyDosageStep()];
  }

  return dosage.map(d => {
    if (!d) {
      return emptyDosageStep();
    }
    const dar = d.doseAndRate?.[0];
    const q = dar?.doseQuantity;
    const r = dar?.doseRange;
    const repeat = d.timing?.repeat;
    // Typed as unknown so we can also tolerate a legacy scalar `when` value
    // (pre-VEG-535 schemas modelled it as a single code rather than code[]).
    const whenRaw: unknown = repeat?.when;
    const when = Array.isArray(whenRaw)
      ? whenRaw.filter((w): w is string => Boolean(w))
      : typeof whenRaw === "string" && whenRaw
        ? [whenRaw]
        : [];
    const customTiming = d.patientInstruction ?? "";
    const asNeeded = Boolean(d.asNeededBoolean);
    // The count is `frequency`; `frequencyMax` is only read so a record written
    // under the old PRN-as-ceiling rule still shows its count in the form.
    const count = repeat?.frequency ?? repeat?.frequencyMax;
    const maxDose = d.maxDosePerPeriod;
    const rate = dar?.rateRatio;

    return {
      doseValue: (q?.value ?? r?.low?.value)?.toString() ?? "",
      doseValueHigh: r?.high?.value?.toString() ?? "",
      doseUnit: q?.unit ?? r?.high?.unit ?? r?.low?.unit ?? "",
      frequencyPreset: getFrequencyPreset(repeat ?? {}),
      frequencyTimes: count?.toString() ?? "",
      frequencyEvery: repeat?.period?.toString() ?? "",
      frequencyEveryHigh: repeat?.periodMax?.toString() ?? "",
      frequencyUnit: repeat?.periodUnit ?? "",
      asNeeded,
      rateValue: rate?.numerator?.value?.toString() ?? "",
      rateUnit: rate?.numerator?.unit ?? "",
      ratePeriodUnit: rate?.denominator?.unit ?? "",
      maxDoseValue: maxDose?.numerator?.value?.toString() ?? "",
      maxDoseUnit: maxDose?.numerator?.unit ?? "",
      maxDosePeriodUnit: maxDose?.denominator?.unit ?? "",
      timing: when,
      customTimingEnabled: Boolean(customTiming),
      customTiming,
      durationValue: repeat?.boundsDuration?.value?.toString() ?? "",
      durationUnit: repeat?.boundsDuration?.unit ?? "",
      // The one-time-only shape's count belongs to the frequency preset, which
      // re-authors it on save; showing it here as well would put the same 1 in
      // two controls and let them disagree.
      totalDoses: storedIsOneTimeOnly(repeat) ? "" : (repeat?.count?.toString() ?? ""),
      // Shown verbatim in the free-text field. No provenance is inferred: the
      // SIG is human-authored either way, and nothing regenerates it.
      text: d.text ?? "",
      additionalInstruction: (d.additionalInstruction ?? []).flatMap(
        concept =>
          concept.coding
            ?.filter(c => c.system === SNOMED_SYSTEM && ADDITIONAL_INSTRUCTION_CODES.has(c.code ?? ""))
            .map(c => c.code as string) ?? []
      ),
    };
  });
};

/** Duration unit options for the form (reuses the frequency period units). */
export const DURATION_UNITS = FREQUENCY_PERIOD_UNITS;
